import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { IT_STATUSES, IT_STATUS_LABELS, type ItEntryDTO, type ItProjectDTO, type ItStatusKey } from "@easyshop/shared";
import { Button, Card, cx, Empty, ErrorState, Loading } from "@/components/ui";
import { useItMutations, useItOverview, type ItEntryInput } from "@/lib/queries";

/**
 * Page IT : suivi des livraisons par projet. Calendrier du mois avec un point par projet
 * les jours où une action commence ou se termine, fil de cartes classé par jour, actions à
 * venir, temps estimé, projets (avec leur dépôt GitHub) et export. Pensée pour le téléphone
 * d'abord ; sur grand écran le calendrier passe à gauche, la liste à droite.
 */

const MONTHS = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
const QUICK: [string, number][] = [
  ["30 min", 30],
  ["1 h", 60],
  ["2 h", 120],
  ["½ journée", 240],
  ["1 journée", 480],
];

const STATUS_CLASS: Record<ItStatusKey, string> = {
  PLANNED: "bg-warn-bg text-warn-ink",
  IN_PROGRESS: "bg-accent-soft text-accent",
  DONE: "bg-positive-bg text-positive",
  LIVE: "bg-positive-bg text-positive",
  BLOCKED: "bg-danger-bg text-danger-ink",
};

// --- Dates ---------------------------------------------------------------------------------

const pad = (n: number) => String(n).padStart(2, "0");
const iso = (y: number, m: number, d: number) => `${y}-${pad(m + 1)}-${pad(d)}`;
const todayIso = () => {
  const n = new Date();
  return iso(n.getFullYear(), n.getMonth(), n.getDate());
};
const parse = (s: string) => {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
};
const fmtShort = (s: string) => parse(s).toLocaleDateString("fr-FR", { day: "numeric", month: "short" }).replace(".", "");
const fmtLong = (s: string) => parse(s).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
const fmtDay = (s: string) => {
  const t = parse(s).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
  return t.charAt(0).toUpperCase() + t.slice(1);
};
const fmtRange = (e: ItEntryDTO) => (e.endDate && e.endDate !== e.startDate ? `${fmtShort(e.startDate)} → ${fmtShort(e.endDate)}` : fmtShort(e.startDate));
export function fmtDur(min: number): string {
  if (!min) return "";
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const r = min % 60;
  return `${h} h${r ? ` ${pad(r)}` : ""}`;
}
const isPlanned = (e: ItEntryDTO) => e.status === "PLANNED" || e.startDate > todayIso();

// --- Page ----------------------------------------------------------------------------------

export function ItPage() {
  const overview = useItOverview();
  const m = useItMutations();
  const now = new Date();
  const [view, setView] = useState({ y: now.getFullYear(), m: now.getMonth() });
  const [projectId, setProjectId] = useState("");
  const [manualOnly, setManualOnly] = useState(false);
  const [soon, setSoon] = useState(false);
  const [day, setDay] = useState("");
  const [editor, setEditor] = useState<ItEntryDTO | "new" | null>(null);
  const [sheet, setSheet] = useState<"projects" | "export" | null>(null);
  const [syncMsg, setSyncMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const placed = useRef(false);

  const data = overview.data;
  const projects = data?.projects ?? [];
  const byId = useMemo(() => new Map(projects.map((p) => [p.id, p])), [projects]);

  // Ouvre le calendrier sur le mois le plus récent qui a des actions (sans dépasser aujourd'hui).
  useEffect(() => {
    if (placed.current || !data?.entries.length) return;
    placed.current = true;
    const t = todayIso();
    const past = data.entries.map((e) => e.startDate).filter((d) => d <= t);
    const pick = past.length ? past.reduce((a, b) => (a > b ? a : b)) : t;
    const [y, mo] = pick.split("-").map(Number);
    setView({ y, m: mo - 1 });
  }, [data]);

  const base = useMemo(
    () => (data?.entries ?? []).filter((e) => (!projectId || e.projectId === projectId) && (!manualOnly || e.source === "MANUAL")),
    [data, projectId, manualOnly]
  );
  const byStart = (a: ItEntryDTO, b: ItEntryDTO) => a.startDate.localeCompare(b.startDate) || a.title.localeCompare(b.title);
  const shown = useMemo(() => {
    if (soon) return base.filter(isPlanned).sort(byStart);
    if (day) return base.filter((e) => e.startDate === day || e.endDate === day).sort(byStart);
    const lo = iso(view.y, view.m, 1);
    const hi = iso(view.y, view.m, new Date(view.y, view.m + 1, 0).getDate());
    return base.filter((e) => (e.endDate ?? e.startDate) >= lo && e.startDate <= hi).sort(byStart);
  }, [base, soon, day, view]);

  const total = shown.reduce((a, e) => a + e.estimateMin, 0);
  const count = `${shown.length} action${shown.length > 1 ? "s" : ""}${total ? ` · ≈ ${fmtDur(total)}` : ""}`;
  const summary = soon ? `À venir · ${count}` : day ? `${fmtDay(day)}${shown.length ? ` · ${count}` : ""}` : `${count} en ${MONTHS[view.m]} ${view.y}`;

  const shift = (n: number) => {
    const d = new Date(view.y, view.m + n, 1);
    setView({ y: d.getFullYear(), m: d.getMonth() });
    setDay("");
    setSoon(false);
  };

  const sync = () => {
    setSyncMsg(null);
    m.sync.mutate(undefined, {
      onSuccess: (r) => {
        if (r.projects.length === 0 && r.errors.length === 0) {
          const why = r.skipped.length ? ` Sans dépôt ou suivi coupé : ${r.skipped.join(", ")}.` : "";
          return setSyncMsg({ ok: false, text: `Rien n'a été lu : aucun projet n'a de dépôt suivi.${why} Ouvrez l'icône dossier pour ajouter le dépôt d'un projet.` });
        }
        const read = r.projects.map((p) => `${p.name} : ${p.commits} commit${p.commits > 1 ? "s" : ""} lu${p.commits > 1 ? "s" : ""}${p.added + p.updated ? `, ${p.added + p.updated} jour${p.added + p.updated > 1 ? "s" : ""} ajouté${p.added + p.updated > 1 ? "s" : ""} ou complété${p.added + p.updated > 1 ? "s" : ""}` : ", rien de nouveau"}`);
        const errs = r.errors.map((e) => `${e.project} : ${e.message}`);
        const skipped = r.skipped.length ? [`Ignorés (sans dépôt ou suivi coupé) : ${r.skipped.join(", ")}`] : [];
        setSyncMsg({ ok: r.errors.length === 0, text: [...read, ...errs, ...skipped].join(". ") + "." });
        if (r.latestDay) {
          // Les nouveautés sont peut-être dans un autre mois que celui affiché.
          const [y, mo] = r.latestDay.split("-").map(Number);
          setView({ y, m: mo - 1 });
          setDay("");
          setSoon(false);
        }
      },
      onError: (e) => setSyncMsg({ ok: false, text: e instanceof Error ? e.message : "Lecture impossible." }),
    });
  };

  if (overview.isLoading) return <Loading />;
  if (overview.error || !data) return <ErrorState error={overview.error ?? new Error("Chargement impossible")} />;

  const colorOf = (e: { projectId: string }) => `var(--it-c${byId.get(e.projectId)?.colorIndex ?? 1})`;

  return (
    <div className="flex flex-col gap-4 pb-[96px] lg:pb-0">
      {/* Filtres et actions */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="-mx-4 flex w-[calc(100%+32px)] gap-2 overflow-x-auto px-4 pb-1 lg:mx-0 lg:w-auto lg:min-w-0 lg:flex-1 lg:flex-wrap lg:px-0" role="group" aria-label="Filtrer">
          <Chip active={!projectId && !manualOnly && !soon} onClick={() => { setProjectId(""); setManualOnly(false); setSoon(false); setDay(""); }}>Tout</Chip>
          <Chip active={soon} onClick={() => { setSoon(!soon); setDay(""); }}>À venir</Chip>
          {projects.map((p) => (
            <Chip key={p.id} active={projectId === p.id} onClick={() => setProjectId(projectId === p.id ? "" : p.id)}>
              <i className="h-[9px] w-[9px] flex-shrink-0 rounded-full" style={{ background: `var(--it-c${p.colorIndex})` }} />
              {p.name}
            </Chip>
          ))}
          <Chip active={manualOnly} onClick={() => setManualOnly(!manualOnly)}>Ajoutés à la main</Chip>
        </div>
        <div className="ml-auto flex flex-shrink-0 items-center gap-2">
          <IconButton label="Projets" onClick={() => setSheet("projects")}><Folder /></IconButton>
          <IconButton label="Exporter" onClick={() => setSheet("export")}><Download /></IconButton>
          <Button onClick={() => setEditor("new")} className="hidden lg:block">+ Ajouter une action</Button>
        </div>
      </div>

      {/* Lecture GitHub */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-[13px] text-muted">
        <span>{data.lastSyncAt ? `Dernière lecture de GitHub : ${fmtLong(data.lastSyncAt.slice(0, 10))}` : "GitHub pas encore lu."}</span>
        {data.githubConnected ? (
          <>
            <Button variant="outline" onClick={sync} disabled={m.sync.isPending}>
              {m.sync.isPending ? "Lecture en cours…" : "Lire GitHub maintenant"}
            </Button>
            {!projects.some((p) => p.repo && p.track) && (
              <span className="font-semibold text-warn-ink">Aucun projet n'a de dépôt suivi : ouvrez l'icône dossier pour en ajouter un.</span>
            )}
          </>
        ) : (
          <span>
            Pour lire GitHub, enregistrez un jeton dans <Link to="/parametres" className="font-semibold text-accent">Paramètres › Page IT</Link>.
          </span>
        )}
      </div>
      {syncMsg && (
        <div role="status" className={cx("rounded-card-sm px-4 py-3 text-[13px] font-semibold", syncMsg.ok ? "bg-positive-bg text-positive" : "bg-danger-bg text-danger-ink")}>
          {syncMsg.text}
        </div>
      )}

      <div className="grid items-start gap-4 lg:grid-cols-[360px_minmax(0,1fr)]">
        <div className="flex flex-col gap-3 lg:sticky lg:top-4">
          <Calendar view={view} onShift={shift} entries={base} projects={byId} day={day} onDay={(d) => { setDay(day === d ? "" : d); setSoon(false); }} />
        </div>

        <div className="flex min-w-0 flex-col gap-2">
          <div className="flex items-center gap-3 px-1">
            <h2 className="flex-1 font-display text-[16px] font-bold">{summary}</h2>
            {(day || soon) && (
              <button type="button" onClick={() => { setDay(""); setSoon(false); }} className="min-h-[40px] text-[14px] font-semibold text-accent">
                Voir le mois
              </button>
            )}
          </div>
          {shown.length === 0 ? (
            <Empty className="rounded-card border border-dashed border-line bg-white">
              {soon ? "Rien de prévu pour le moment." : day ? "Rien ne commence ni ne se termine ce jour-là." : "Rien pour ce mois avec ces filtres."}
              <br />
              Touchez + pour ajouter une action.
            </Empty>
          ) : (
            <EntryList entries={shown} grouped={!day} colorOf={colorOf} projects={byId} onEdit={setEditor} onDelete={(id) => m.deleteEntry.mutate(id)} />
          )}
          {m.deleteEntry.error && <ErrorState error={m.deleteEntry.error} />}
        </div>
      </div>

      {/* Bouton + flottant (téléphone) : au-dessus de la barre d'onglets, à droite. */}
      <button
        type="button"
        aria-label="Ajouter une action"
        onClick={() => setEditor("new")}
        className="fixed right-4 z-[24] flex h-[56px] w-[56px] items-center justify-center rounded-full bg-accent text-white shadow-menu lg:hidden"
        style={{ bottom: "calc(92px + env(safe-area-inset-bottom))" }}
      >
        <Plus />
      </button>

      {editor && (
        <EntrySheet
          key={editor === "new" ? "new" : editor.id}
          entry={editor === "new" ? null : editor}
          projects={projects}
          defaultProjectId={projectId || projects[0]?.id || ""}
          defaultDay={day || todayIso()}
          onClose={() => setEditor(null)}
          onSaved={(startDate) => {
            const [y, mo] = startDate.split("-").map(Number);
            setView({ y, m: mo - 1 });
            setDay("");
            setSoon(false);
            setEditor(null);
          }}
        />
      )}
      {sheet === "projects" && <ProjectsSheet projects={projects} entries={data.entries} githubConnected={data.githubConnected} onClose={() => setSheet(null)} />}
      {sheet === "export" && <ExportSheet entries={shown} projects={byId} period={soon ? "à venir" : day ? fmtLong(day) : `${MONTHS[view.m]} ${view.y}`} onClose={() => setSheet(null)} />}
    </div>
  );
}

// --- Petits éléments -----------------------------------------------------------------------

function Chip({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cx(
        "flex min-h-[40px] flex-shrink-0 items-center gap-[7px] whitespace-nowrap rounded-full border px-[14px] text-[14px] font-medium",
        active ? "border-accent bg-accent text-white" : "border-line bg-white text-ink hover:bg-accent-row"
      )}
    >
      {children}
    </button>
  );
}

function IconButton({ label, onClick, children }: { label: string; onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" aria-label={label} title={label} onClick={onClick} className="flex h-[44px] w-[44px] items-center justify-center rounded-full border border-line bg-white text-ink hover:bg-accent-row">
      {children}
    </button>
  );
}

const Svg = ({ d, size = 20 }: { d: ReactNode; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    {d}
  </svg>
);
const Folder = () => <Svg d={<path d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />} />;
const Download = () => <Svg d={<path d="M12 3v12M7 10l5 5 5-5M5 21h14" />} />;
const Plus = () => <Svg size={26} d={<path d="M12 5v14M5 12h14" />} />;
const Pencil = () => <Svg size={18} d={<><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 013 3L7 19l-4 1 1-4z" /></>} />;
const Trash = () => <Svg size={18} d={<><path d="M3 6h18" /><path d="M8 6V4h8v2" /><path d="M19 6l-1 14H6L5 6" /></>} />;
const Clock = () => <Svg size={16} d={<><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>} />;
const CalIcon = () => <Svg size={16} d={<><rect x="3" y="5" width="18" height="16" rx="3" /><path d="M3 10h18M8 3v4M16 3v4" /></>} />;
const Sync = () => <Svg size={16} d={<><path d="M21 12a9 9 0 11-3-6.7" /><path d="M21 4v5h-5" /></>} />;
const Person = () => <Svg size={16} d={<><circle cx="12" cy="8" r="4" /><path d="M4 21c0-4 4-6 8-6s8 2 8 6" /></>} />;

// --- Calendrier ----------------------------------------------------------------------------

function Calendar({
  view, onShift, entries, projects, day, onDay,
}: {
  view: { y: number; m: number };
  onShift: (n: number) => void;
  entries: ItEntryDTO[];
  projects: Map<string, ItProjectDTO>;
  day: string;
  onDay: (d: string) => void;
}) {
  const marks = useMemo(() => {
    const out = new Map<string, Set<string>>();
    for (const e of entries) for (const d of [e.startDate, e.endDate]) if (d) (out.get(d) ?? out.set(d, new Set()).get(d)!).add(e.projectId);
    return out;
  }, [entries]);
  const lead = (new Date(view.y, view.m, 1).getDay() + 6) % 7;
  const days = new Date(view.y, view.m + 1, 0).getDate();
  const today = todayIso();
  return (
    <Card pad="px-3 pb-3 pt-3">
      <div className="flex items-center gap-1">
        <button type="button" aria-label="Mois précédent" onClick={() => onShift(-1)} className="flex h-[40px] w-[40px] items-center justify-center rounded-full text-ink hover:bg-accent-row"><Svg d={<path d="M15 18l-6-6 6-6" />} /></button>
        <h2 className="flex-1 text-center font-display text-[17px] font-bold capitalize">{MONTHS[view.m]} {view.y}</h2>
        <button type="button" aria-label="Mois suivant" onClick={() => onShift(1)} className="flex h-[40px] w-[40px] items-center justify-center rounded-full text-ink hover:bg-accent-row"><Svg d={<path d="M9 18l6-6-6-6" />} /></button>
      </div>
      <div className="mt-2 grid grid-cols-7 gap-[2px]">
        {["L", "M", "M", "J", "V", "S", "D"].map((w, i) => (
          <div key={i} aria-hidden className="py-1 text-center text-[11px] font-semibold text-muted-2">{w}</div>
        ))}
        {Array.from({ length: lead }, (_, i) => <div key={`b${i}`} />)}
        {Array.from({ length: days }, (_, i) => {
          const k = iso(view.y, view.m, i + 1);
          const ids = [...(marks.get(k) ?? [])].slice(0, 3);
          const sel = k === day;
          return (
            <button
              key={k}
              type="button"
              aria-pressed={sel}
              aria-label={`${fmtDay(k)}${ids.length ? `, ${ids.length} projet${ids.length > 1 ? "s" : ""}` : ""}`}
              onClick={() => onDay(k)}
              className={cx("flex min-h-[46px] flex-col items-center justify-between rounded-[12px] pb-1 pt-[5px] text-[15px] font-medium", sel && "bg-accent-soft", !ids.length && !sel && "text-muted")}
            >
              <span className={cx("flex h-[28px] w-[28px] items-center justify-center rounded-full tabular", sel && "bg-accent text-white", !sel && k === today && "ring-[1.5px] ring-inset ring-accent")}>{i + 1}</span>
              <span className="flex h-[6px] gap-[3px]">
                {ids.map((id) => <i key={id} className="h-[6px] w-[6px] rounded-full" style={{ background: `var(--it-c${projects.get(id)?.colorIndex ?? 1})` }} />)}
              </span>
            </button>
          );
        })}
      </div>
      <p className="mt-2 text-center text-[12.5px] text-muted">Un point = une action qui commence ou se termine ce jour-là. Touchez un jour pour la voir.</p>
    </Card>
  );
}

// --- Cartes --------------------------------------------------------------------------------

function EntryList({
  entries, grouped, colorOf, projects, onEdit, onDelete,
}: {
  entries: ItEntryDTO[];
  grouped: boolean;
  colorOf: (e: ItEntryDTO) => string;
  projects: Map<string, ItProjectDTO>;
  onEdit: (e: ItEntryDTO) => void;
  onDelete: (id: string) => void;
}) {
  let last = "";
  return (
    <>
      {entries.map((e) => {
        const head = grouped && e.startDate !== last ? ((last = e.startDate), <h3 key={`h${e.id}`} className="mx-1 mb-[2px] mt-3 text-[12.5px] font-semibold uppercase tracking-[0.06em] text-muted-2">{fmtDay(e.startDate)}</h3>) : null;
        return (
          <div key={e.id} className="contents">
            {head}
            <EntryCard e={e} color={colorOf(e)} project={projects.get(e.projectId)?.name ?? ""} onEdit={() => onEdit(e)} onDelete={() => onDelete(e.id)} />
          </div>
        );
      })}
    </>
  );
}

function EntryCard({ e, color, project, onEdit, onDelete }: { e: ItEntryDTO; color: string; project: string; onEdit: () => void; onDelete: () => void }) {
  const [open, setOpen] = useState(false);
  const [ask, setAsk] = useState(false);
  useEffect(() => {
    if (!ask) return;
    const t = setTimeout(() => setAsk(false), 4000);
    return () => clearTimeout(t);
  }, [ask]);
  const visible = open ? e.items : e.items.slice(0, 2);
  return (
    <article className={cx("rounded-card-mob border bg-white px-4 py-[14px]", isPlanned(e) ? "border-dashed border-ios-chevron" : "border-line")}>
      <div className="flex min-h-[32px] items-center gap-2">
        <span className="flex min-w-0 flex-1 items-center gap-2 text-[13px] font-semibold text-muted">
          <i className="h-[9px] w-[9px] flex-shrink-0 rounded-full" style={{ background: color }} />
          <span className="truncate">{project}</span>
        </span>
        <button type="button" aria-label="Modifier" onClick={onEdit} className="-my-1 flex h-9 w-9 items-center justify-center rounded-[10px] text-muted hover:bg-accent-soft hover:text-accent"><Pencil /></button>
        {ask ? (
          <button type="button" onClick={onDelete} className="-my-1 h-9 rounded-[10px] bg-danger-bg px-[10px] text-[13px] font-semibold text-danger-ink">Supprimer ?</button>
        ) : (
          <button type="button" aria-label="Supprimer" onClick={() => setAsk(true)} className="-my-1 flex h-9 w-9 items-center justify-center rounded-[10px] text-muted hover:bg-danger-bg hover:text-danger"><Trash /></button>
        )}
      </div>
      <h4 className="mt-[6px] font-display text-[17px] font-bold leading-[22px]">{e.title}</h4>
      {e.client && <p className="text-[14px] text-muted">{e.client}</p>}
      <ul className="mt-2 flex list-disc flex-col gap-[5px] pl-5 text-[15px]">
        {visible.map((t, i) => <li key={i}>{t}</li>)}
      </ul>
      {e.items.length > 2 && (
        <button type="button" onClick={() => setOpen(!open)} className="min-h-[36px] pt-2 text-[14px] font-semibold text-accent">
          {open ? "Réduire" : `Voir tout (${e.items.length})`}
        </button>
      )}
      {e.note && <p className="mt-2 text-[14px] text-muted">{e.note}</p>}
      <div className="mt-3 flex flex-wrap items-center gap-x-[14px] gap-y-2 border-t border-line-soft pt-3 text-[13px] text-muted">
        <span className="flex items-center gap-[6px]"><CalIcon />{fmtRange(e)}</span>
        {e.estimateMin > 0 && <span className="flex items-center gap-[6px]"><Clock />≈ {fmtDur(e.estimateMin)}</span>}
        <span className="flex-1" />
        <span className={cx("rounded-full px-[11px] py-[3px] text-[13px] font-semibold", STATUS_CLASS[e.status])}>{IT_STATUS_LABELS[e.status]}</span>
        <span className="flex items-center gap-[6px]" title={e.source === "MANUAL" ? "Ajouté à la main" : "Lu automatiquement sur GitHub"}>
          {e.source === "MANUAL" ? <Person /> : <Sync />}
          {e.source === "MANUAL" ? "À la main" : "Auto"}
        </span>
      </div>
    </article>
  );
}

// --- Feuille (téléphone : monte du bas ; grand écran : fenêtre centrée) --------------------

function Sheet({ title, onClose, children, footer }: { title: string; onClose: () => void; children: ReactNode; footer?: ReactNode }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-[60]">
      <div className="absolute inset-0 bg-[rgba(20,30,20,0.45)]" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="absolute inset-x-0 bottom-0 mx-auto flex max-h-[calc(100dvh-env(safe-area-inset-top)-8px)] w-full max-w-[560px] flex-col overflow-hidden rounded-t-[22px] bg-white shadow-drawer md:bottom-auto md:top-1/2 md:-translate-y-1/2 md:rounded-[22px]"
      >
        <div className="flex-shrink-0 px-[18px] pb-2 pt-[10px]">
          <div className="mx-auto mb-3 h-[5px] w-[44px] rounded-full bg-line md:hidden" />
          <h2 className="font-display text-[20px] font-bold">{title}</h2>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-[18px] pb-4 pt-2">{children}</div>
        {footer && <div className="flex-shrink-0 border-t border-line bg-white px-[18px] pb-[calc(14px+env(safe-area-inset-bottom))] pt-3">{footer}</div>}
      </div>
    </div>
  );
}

const field = "h-[48px] w-full min-w-0 appearance-none rounded-[12px] border border-line bg-page px-[13px] text-[16px] focus:border-accent";

function Field({ label, htmlFor, children, hint }: { label: string; htmlFor?: string; children: ReactNode; hint?: string }) {
  return (
    <div className="mb-4 flex min-w-0 flex-col gap-[6px]">
      <label htmlFor={htmlFor} className="text-[13px] font-semibold text-muted">{label}</label>
      {children}
      {hint && <span className="text-[12.5px] text-muted">{hint}</span>}
    </div>
  );
}

// --- Fiche d'une action --------------------------------------------------------------------

function EntrySheet({
  entry, projects, defaultProjectId, defaultDay, onClose, onSaved,
}: {
  entry: ItEntryDTO | null;
  projects: ItProjectDTO[];
  defaultProjectId: string;
  defaultDay: string;
  onClose: () => void;
  onSaved: (startDate: string) => void;
}) {
  const m = useItMutations();
  const start0 = entry?.startDate ?? defaultDay;
  const [projectId, setProjectId] = useState(entry?.projectId ?? defaultProjectId);
  const [title, setTitle] = useState(entry?.title ?? "");
  const [client, setClient] = useState(entry?.client ?? "");
  const [items, setItems] = useState(entry?.items.join("\n") ?? "");
  const [start, setStart] = useState(start0);
  const [end, setEnd] = useState(entry?.endDate ?? "");
  const [status, setStatus] = useState<ItStatusKey>(entry?.status ?? (start0 > todayIso() ? "PLANNED" : "DONE"));
  const [touched, setTouched] = useState(Boolean(entry));
  const [est, setEst] = useState(entry?.estimateMin ? String(Math.round((entry.estimateMin / 60) * 100) / 100).replace(".", ",") : "");
  const [note, setNote] = useState(entry?.note ?? "");
  const [error, setError] = useState<string | null>(null);

  const estMin = (() => {
    const h = parseFloat(est.trim().replace(",", "."));
    return h > 0 ? Math.round(h * 60) : 0;
  })();
  const pending = m.createEntry.isPending || m.updateEntry.isPending;

  const submit = (ev: FormEvent) => {
    ev.preventDefault();
    const lines = items.split("\n").map((s) => s.trim()).filter(Boolean);
    if (!projectId) return setError("Choisissez un projet. Ajoutez-en un avec l'icône dossier si besoin.");
    if (!title.trim() || !lines.length || !start) return setError("Le titre, au moins une action et la date de début sont nécessaires.");
    if (end && end < start) return setError("La date de fin ne peut pas être avant le début.");
    if (est.trim() && !estMin) return setError("Le temps estimé doit être un nombre d'heures, par exemple 2 ou 1,5.");
    setError(null);
    const input: ItEntryInput = { projectId, title: title.trim(), client: client.trim() || null, items: lines, startDate: start, endDate: end || null, status, note: note.trim() || null, estimateMin: estMin };
    const opts = { onSuccess: () => onSaved(start), onError: (e: unknown) => setError(e instanceof Error ? e.message : "Enregistrement impossible.") };
    if (entry) m.updateEntry.mutate({ ...input, id: entry.id }, opts);
    else m.createEntry.mutate(input, opts);
  };

  return (
    <Sheet
      title={entry ? "Modifier l'action" : "Nouvelle action"}
      onClose={onClose}
      footer={
        <>
          {error && <p role="alert" className="mb-2 text-[14px] font-semibold text-danger">{error}</p>}
          <div className="flex gap-[10px]">
            <button type="button" onClick={onClose} className="h-[50px] flex-1 rounded-[14px] border border-line bg-white text-[16px] font-semibold">Annuler</button>
            <button type="submit" form="it-entry-form" disabled={pending} className="h-[50px] flex-1 rounded-[14px] bg-accent text-[16px] font-semibold text-white disabled:opacity-50">
              {pending ? "Enregistrement…" : "Enregistrer"}
            </button>
          </div>
        </>
      }
    >
      <form id="it-entry-form" onSubmit={submit} noValidate>
        <Field label="Projet" htmlFor="it-project">
          <select id="it-project" className={field} value={projectId} onChange={(e) => setProjectId(e.target.value)}>
            {projects.length === 0 && <option value="">Aucun projet : ajoutez-en un</option>}
            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </Field>
        <Field label="Titre" htmlFor="it-title">
          <input id="it-title" className={field} value={title} maxLength={160} placeholder="Ex. Installation de la balance" onChange={(e) => setTitle(e.target.value)} />
        </Field>
        <Field label="Client ou lieu (facultatif)" htmlFor="it-client">
          <input id="it-client" className={field} value={client} maxLength={120} placeholder="Ex. Café Dupont" onChange={(e) => setClient(e.target.value)} />
        </Field>
        <Field label="Ce qu'on fait" htmlFor="it-items" hint="Une ligne = une puce dans la carte. Vous pouvez écrire ce qui est prévu avec le client.">
          <textarea id="it-items" className={cx(field, "h-auto min-h-[110px] py-[11px] leading-[1.45]")} value={items} placeholder="Une action par ligne" onChange={(e) => setItems(e.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Début" htmlFor="it-start">
            <input
              id="it-start"
              type="date"
              className={field}
              value={start}
              onChange={(e) => {
                setStart(e.target.value);
                if (!entry && !touched) setStatus(e.target.value > todayIso() ? "PLANNED" : "DONE");
              }}
            />
          </Field>
          <Field label="Fin (facultatif)" htmlFor="it-end">
            <input id="it-end" type="date" className={field} value={end} onChange={(e) => setEnd(e.target.value)} />
          </Field>
        </div>
        <Field label="Où en est-on ?" htmlFor="it-status">
          <select id="it-status" className={field} value={status} onChange={(e) => { setStatus(e.target.value as ItStatusKey); setTouched(true); }}>
            {IT_STATUSES.map((s) => <option key={s} value={s}>{IT_STATUS_LABELS[s]}</option>)}
          </select>
        </Field>
        <div className="mb-4 flex flex-col gap-[6px]">
          <span id="it-est-l" className="text-[13px] font-semibold text-muted">Temps estimé</span>
          <div className="flex flex-wrap gap-2" role="group" aria-labelledby="it-est-l">
            {QUICK.map(([label, min]) => (
              <button
                key={min}
                type="button"
                aria-pressed={estMin === min}
                onClick={() => setEst(String(min / 60).replace(".", ","))}
                className={cx("min-h-[38px] rounded-full border px-[14px] text-[14px] font-medium", estMin === min ? "border-accent bg-accent-soft text-accent" : "border-line bg-page")}
              >
                {label}
              </button>
            ))}
          </div>
          <input id="it-est" inputMode="decimal" autoComplete="off" aria-label="Temps estimé en heures" className={field} value={est} placeholder="Autre durée en heures, ex. 2,5" onChange={(e) => setEst(e.target.value)} />
        </div>
        <Field label="Précision (facultatif)" htmlFor="it-note">
          <input id="it-note" className={field} value={note} maxLength={300} placeholder="Ex. à faire sur place" onChange={(e) => setNote(e.target.value)} />
        </Field>
      </form>
    </Sheet>
  );
}

// --- Projets -------------------------------------------------------------------------------

function ProjectsSheet({ projects, entries, githubConnected, onClose }: { projects: ItProjectDTO[]; entries: ItEntryDTO[]; githubConnected: boolean; onClose: () => void }) {
  const m = useItMutations();
  const [name, setName] = useState("");
  const [repo, setRepo] = useState("");
  const [ask, setAsk] = useState<string | null>(null);
  const [editRepo, setEditRepo] = useState<{ id: string; value: string } | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const fail = (e: unknown) => setMsg({ ok: false, text: e instanceof Error ? e.message : "Action impossible." });
  const saveRepo = () => {
    if (!editRepo) return;
    m.updateProject.mutate({ id: editRepo.id, repo: editRepo.value.trim() }, { onSuccess: () => { setEditRepo(null); setMsg({ ok: true, text: "Dépôt enregistré." }); }, onError: fail });
  };
  const add = (ev: FormEvent) => {
    ev.preventDefault();
    if (!name.trim()) return setMsg({ ok: false, text: "Donnez un nom au projet." });
    m.createProject.mutate({ name: name.trim(), repo: repo.trim() }, { onSuccess: () => { setName(""); setRepo(""); setMsg({ ok: true, text: "Projet ajouté." }); }, onError: fail });
  };
  return (
    <Sheet title="Projets suivis" onClose={onClose} footer={<button type="button" onClick={onClose} className="h-[50px] w-full rounded-[14px] border border-line bg-white text-[16px] font-semibold">Fermer</button>}>
      {projects.length === 0 && <p className="py-2 text-[13px] text-muted">Aucun projet pour le moment.</p>}
      {projects.map((p) => {
        const n = entries.filter((e) => e.projectId === p.id).length;
        return (
          <div key={p.id} className="flex flex-wrap items-center gap-[10px] border-b border-line-soft py-3">
            <i className="h-[10px] w-[10px] flex-shrink-0 rounded-full" style={{ background: `var(--it-c${p.colorIndex})` }} />
            <div className="min-w-0 flex-1">
              <b className="block text-[16px]">{p.name}</b>
              <small className="block break-all text-[12px] text-muted">{p.repo ?? "Pas de dépôt GitHub"} · {n} action{n > 1 ? "s" : ""}</small>
              {editRepo?.id !== p.id && (
                <button type="button" onClick={() => setEditRepo({ id: p.id, value: p.repo ?? "" })} className="mt-1 min-h-[32px] text-[13px] font-semibold text-accent">
                  {p.repo ? "Changer le dépôt" : "Ajouter un dépôt GitHub"}
                </button>
              )}
            </div>
            <button
              type="button"
              disabled={!p.repo || m.updateProject.isPending}
              aria-pressed={p.track && Boolean(p.repo)}
              onClick={() => m.updateProject.mutate({ id: p.id, track: !p.track }, { onError: fail })}
              className={cx("min-h-[36px] whitespace-nowrap rounded-full border px-3 text-[13px] font-semibold disabled:opacity-45", p.track && p.repo ? "border-positive bg-positive-bg text-positive" : "border-line bg-page text-muted")}
            >
              Suivi {p.track && p.repo ? "actif" : "coupé"}
            </button>
            {ask === p.id ? (
              <button type="button" onClick={() => { setAsk(null); m.deleteProject.mutate(p.id, { onError: fail }); }} className="h-9 rounded-[10px] bg-danger-bg px-[10px] text-[13px] font-semibold text-danger-ink">Retirer ?</button>
            ) : (
              <button type="button" aria-label="Retirer le projet" onClick={() => { setAsk(p.id); setTimeout(() => setAsk((a) => (a === p.id ? null : a)), 4000); }} className="flex h-9 w-9 items-center justify-center rounded-[10px] text-muted hover:bg-danger-bg hover:text-danger"><Trash /></button>
            )}
            {editRepo?.id === p.id && (
              <div className="flex basis-full gap-2 pb-1 pl-5">
                <input aria-label={`Dépôt GitHub de ${p.name}`} className="h-[44px] w-0 min-w-0 flex-1 rounded-[10px] border border-line bg-page px-3 text-[16px]" value={editRepo.value} autoComplete="off" autoCapitalize="none" spellCheck={false} placeholder="propriétaire/dépôt" onChange={(e) => setEditRepo({ id: p.id, value: e.target.value })} />
                <button type="button" disabled={m.updateProject.isPending} onClick={saveRepo} className="h-[44px] shrink-0 rounded-[10px] bg-accent px-4 text-[14px] font-semibold text-white disabled:opacity-50">OK</button>
                <button type="button" onClick={() => setEditRepo(null)} className="h-[44px] shrink-0 rounded-[10px] border border-line bg-white px-3 text-[14px] font-semibold">Annuler</button>
              </div>
            )}
          </div>
        );
      })}
      <form onSubmit={add} className="mt-5" noValidate>
        <h3 className="mb-3 font-display text-[16px] font-bold">Ajouter un projet</h3>
        <Field label="Nom" htmlFor="it-p-name"><input id="it-p-name" className={field} value={name} maxLength={80} autoComplete="off" placeholder="Ex. Application livraison" onChange={(e) => setName(e.target.value)} /></Field>
        <Field label="Dépôt GitHub (facultatif)" htmlFor="it-p-repo" hint="propriétaire/dépôt, ou l'adresse du dépôt collée telle quelle.">
          <input id="it-p-repo" className={field} value={repo} autoComplete="off" autoCapitalize="none" spellCheck={false} placeholder="propriétaire/dépôt" onChange={(e) => setRepo(e.target.value)} />
        </Field>
        <Button type="submit" disabled={m.createProject.isPending} className="!h-[50px] w-full !rounded-[14px] !text-[16px]">Ajouter le projet</Button>
        {msg && <p role="status" className={cx("mt-3 text-[14px]", msg.ok ? "text-positive" : "font-semibold text-danger")}>{msg.text}</p>}
      </form>
      <div className="mt-5 rounded-[14px] bg-accent-soft px-[14px] py-3 text-[14px]">
        <b>Comment fonctionne le suivi GitHub.</b> Quand « Suivi » est actif, le bouton « Lire GitHub maintenant » transforme les nouveaux commits du dépôt en une action par jour.{" "}
        {githubConnected ? "Le jeton du réseau est enregistré." : <>Il faut d'abord enregistrer un jeton dans <Link to="/parametres" className="font-semibold text-accent">Paramètres › Page IT</Link>.</>}
      </div>
    </Sheet>
  );
}

// --- Export --------------------------------------------------------------------------------

const csvCell = (v: unknown) => `"${String(v ?? "").replace(/"/g, '""')}"`;
const esc = (s: string) => s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);

function groupByProject(entries: ItEntryDTO[], projects: Map<string, ItProjectDTO>) {
  const by = new Map<string, ItEntryDTO[]>();
  for (const e of entries) (by.get(e.projectId) ?? by.set(e.projectId, []).get(e.projectId)!).push(e);
  return [...by.entries()]
    .map(([id, list]) => ({ project: projects.get(id), list }))
    .sort((a, b) => (a.project?.position ?? 99) - (b.project?.position ?? 99));
}

export function buildExport(kind: "csv" | "md" | "html", entries: ItEntryDTO[], projects: Map<string, ItProjectDTO>, period: string): string {
  const name = (e: ItEntryDTO) => projects.get(e.projectId)?.name ?? "";
  if (kind === "csv") {
    const rows = [["Projet", "Titre", "Client", "Ce qui a été fait", "Début", "Fin", "Statut", "Précision"].map(csvCell).join(";")];
    for (const e of entries) rows.push([name(e), e.title, e.client, e.items.join(" | "), e.startDate, e.endDate, IT_STATUS_LABELS[e.status], e.note].map(csvCell).join(";"));
    return `﻿${rows.join("\r\n")}`;
  }
  const groups = groupByProject(entries, projects);
  if (kind === "md") {
    const out = ["# Suivi des livraisons", "", `Période : ${period}`, ""];
    for (const g of groups) {
      out.push(`## ${g.project?.name ?? "Sans projet"}`, "", "| Titre | Ce qui a été fait | Dates | Statut |", "|---|---|---|---|");
      for (const e of g.list) out.push(`| ${[`${e.title}${e.client ? ` (${e.client})` : ""}`, `${e.items.join(" ; ")}${e.note ? ` — ${e.note}` : ""}`, fmtRange(e), IT_STATUS_LABELS[e.status]].map((c) => c.replace(/\|/g, "\\|")).join(" | ")} |`);
      out.push("");
    }
    return out.join("\n");
  }
  const css = "body{font:15px/1.5 system-ui,sans-serif;color:#1d2a20;max-width:900px;margin:24px auto;padding:0 16px}h1{font-size:26px}h2{font-size:18px;margin-top:26px}table{border-collapse:collapse;width:100%}th,td{border:1px solid #dfe6e0;padding:8px 10px;text-align:left;vertical-align:top}th{background:#e8f3ea;font-size:12px;text-transform:uppercase;letter-spacing:.05em}ul{margin:0;padding-left:18px}small{color:#5d6b60}";
  let h = `<!doctype html><html lang="fr"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><title>Suivi des livraisons</title><style>${css}</style></head><body><h1>Suivi des livraisons</h1><p><small>Période : ${esc(period)}</small></p>`;
  for (const g of groups) {
    h += `<h2>${esc(g.project?.name ?? "Sans projet")}</h2><div style="overflow-x:auto"><table><thead><tr><th>Titre</th><th>Ce qui a été fait</th><th>Dates</th><th>Statut</th></tr></thead><tbody>`;
    for (const e of g.list) h += `<tr><td>${esc(e.title)}${e.client ? `<br><small>${esc(e.client)}</small>` : ""}</td><td><ul>${e.items.map((i) => `<li>${esc(i)}</li>`).join("")}</ul>${e.note ? `<small>${esc(e.note)}</small>` : ""}</td><td>${esc(fmtRange(e))}</td><td>${esc(IT_STATUS_LABELS[e.status])}</td></tr>`;
    h += "</tbody></table></div>";
  }
  return `${h}</body></html>`;
}

const MIME = { csv: "text/csv;charset=utf-8", md: "text/markdown;charset=utf-8", html: "text/html;charset=utf-8" } as const;

function ExportSheet({ entries: shown, projects, period, onClose }: { entries: ItEntryDTO[]; projects: Map<string, ItProjectDTO>; period: string; onClose: () => void }) {
  const [msg, setMsg] = useState<string | null>(null);
  // Le document ne montre que ce qui a été fait : ni l'à venir, ni les temps estimés.
  const entries = useMemo(() => shown.filter((e) => e.status === "DONE" || e.status === "LIVE"), [shown]);
  const go = (kind: "csv" | "md" | "html") => {
    if (!entries.length) return setMsg("Aucune action terminée ou en ligne avec cette sélection.");
    const url = URL.createObjectURL(new Blob([buildExport(kind, entries, projects, period)], { type: MIME[kind] }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `suivi-livraisons-${todayIso()}.${kind}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
    setMsg("Fichier enregistré.");
  };
  const opt = (kind: "csv" | "html" | "md", t: string, d: string) => (
    <button type="button" onClick={() => go(kind)} className="mb-[10px] flex min-h-[64px] w-full flex-col items-start justify-center rounded-[14px] border border-line bg-page px-4 text-left">
      <b className="text-[16px]">{t}</b>
      <span className="text-[13px] text-muted">{d}</span>
    </button>
  );
  return (
    <Sheet title="Exporter" onClose={onClose} footer={<button type="button" onClick={onClose} className="h-[50px] w-full rounded-[14px] border border-line bg-white text-[16px] font-semibold">Fermer</button>}>
      <p className="mb-[14px] text-[13px] text-muted">Export de ce qui a été fait ({entries.length} action{entries.length > 1 ? "s" : ""} terminée{entries.length > 1 ? "s" : ""} ou en ligne) : {period}.</p>
      {opt("csv", "Tableur", "Fichier CSV, s'ouvre dans Excel")}
      {opt("html", "Page web", "Fichier HTML, prêt à envoyer")}
      {opt("md", "Texte", "Fichier Markdown, pour un document")}
      {msg && <p role="status" className="text-[14px] text-muted">{msg}</p>}
    </Sheet>
  );
}
